import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, ChevronDown, Loader2, Share2, Trash2 } from 'lucide-react';
import useUserUuid from './useUserUuid';
import SharePopover, { buildShareText, getSenderName } from './SharePopover';

import { getBackendUrl } from '../../lib/runtimeConfig';

const API = `${getBackendUrl()}/api`;

// Bot-friendly meta URL for crawler unfurls. /api/trip/{slug}/meta serves
// OG/Twitter tags + static map image then meta-refreshes humans to the SPA
// at /trip/{slug}. Crawlers landing on the raw SPA see only the generic
// hero image, which is why we share this URL instead.
const buildShareUrl = (slug) => `${window.location.origin}/api/trip/${slug}/meta`;

/**
 * SaveTripBar — single "Save & Share" entry point under the planner card.
 *
 * Mental model: ONE button drives the entire save → share → email flow.
 *   - Pre-save:  click → POST the trip, store the slug, open SharePopover.
 *   - Post-save: click → just open SharePopover (no duplicate save).
 *
 * Everything previously split across three widgets (Save button, "View /
 * share" link, "Email me this trip" row) now lives inside the popover.
 */
const SaveTripBar = ({ submittedQuery, results, chatHistory }) => {
  const { uuid, myTrips, recordTrip, removeTrip } = useUserUuid();
  const [saving, setSaving] = useState(false);
  const [savedSlug, setSavedSlug] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [openTrips, setOpenTrips] = useState(false);
  const [deletingSlug, setDeletingSlug] = useState(null);
  const wrapRef = useRef(null);

  // Reset the "saved" badge when the itinerary changes (new turn / new trip).
  useEffect(() => {
    setSavedSlug(null);
    setPopoverOpen(false);
  }, [submittedQuery]);

  // Close my-trips dropdown on outside-click (popover handles its own).
  useEffect(() => {
    if (!openTrips) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpenTrips(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openTrips]);

  const handleDelete = async (slug, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!slug || !uuid || deletingSlug) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this saved trip? This can\u2019t be undone.')) return;
    setDeletingSlug(slug);
    try {
      await axios.delete(`${API}/ai-search/trips/${slug}`, {
        params: { user_uuid: uuid },
        timeout: 10000,
      });
      removeTrip(slug);
      if (savedSlug === slug) setSavedSlug(null);
      toast.success('Trip deleted');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        removeTrip(slug);
        toast.success('Trip removed');
      } else {
        toast.error('Could not delete trip — please try again.');
      }
    } finally {
      setDeletingSlug(null);
    }
  };

  // Idempotent: returns the existing slug if the trip is already saved,
  // otherwise POSTs and stores the new one. Surfaces a toast on failure.
  const ensureSaved = async () => {
    if (savedSlug) return savedSlug;
    if (!uuid || results.length === 0) return null;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/ai-search/trips`,
        {
          user_uuid: uuid,
          query: submittedQuery || 'Wine country trip',
          itinerary: results.slice(0, 3),
          chat_history: Array.isArray(chatHistory) ? chatHistory : [],
        },
        { timeout: 12000 },
      );
      const { slug, created_at } = res.data || {};
      if (!slug) throw new Error('no slug');
      recordTrip({ slug, query: submittedQuery || 'Wine country trip', created_at });
      setSavedSlug(slug);
      return slug;
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 503 ? 'Save unavailable — please retry shortly.' : 'Could not save trip right now.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Try the OS native share sheet first — that gives the user every
  // installed app (WhatsApp, iMessage, Slack, Telegram, Discord, AirDrop,
  // etc.) rather than our curated 5-icon fallback. Only when native share
  // is genuinely unavailable / blocked do we drop into the popover.
  const tryNativeShareThenFallback = async (slugForShare, queryText) => {
    if (!slugForShare) return;
    const url = `${window.location.origin}/api/trip/${slugForShare}/meta`;
    // Personalised share text — uses the sender's saved name if they've
    // added one ("Lalit shared a wine country itinerary with you").
    // Otherwise falls back to a clean generic.
    const text = buildShareText({ sender: getSenderName(), query: queryText });
    const payload = { title: text, text, url };
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      const ok = (() => {
        if (typeof navigator.canShare === 'function') {
          try { return navigator.canShare(payload); } catch { return false; }
        }
        return true;
      })();
      if (ok) {
        try {
          await navigator.share(payload);
          return; // success — OS handled it, no popover needed
        } catch (err) {
          if (err?.name === 'AbortError') return; // user cancelled — also no popover
          // anything else: fall through to popover
        }
      }
    }
    // Native unavailable or blocked → show the popover fallback with the
    // 5-icon grid + copy / email / open-page.
    setPopoverOpen(true);
  };

  const handleShareClick = async () => {
    const slug = await ensureSaved();
    if (!slug) return;
    await tryNativeShareThenFallback(slug, submittedQuery);
  };

  const buttonLabel = saving ? 'Saving…' : savedSlug ? 'Share' : 'Save & Share';
  const isDisabled = saving || results.length === 0;
  const shareUrl = savedSlug ? buildShareUrl(savedSlug) : '';

  return (
    <div className="relative flex items-center justify-between gap-2 flex-wrap" data-testid="save-trip-bar" ref={wrapRef}>
      <div className="relative">
        <button
          type="button"
          onClick={handleShareClick}
          disabled={isDisabled}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-wine-600 hover:bg-wine-700 disabled:opacity-60 disabled:cursor-not-allowed px-3.5 py-1.5 rounded-md transition-colors"
          data-testid="save-trip-button"
          aria-haspopup="dialog"
          aria-expanded={popoverOpen}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
          {buttonLabel}
          {savedSlug && <Check className="w-3 h-3 ml-1 text-green-300" data-testid="save-trip-saved-check" />}
        </button>

        <SharePopover
          open={popoverOpen}
          onClose={() => setPopoverOpen(false)}
          slug={savedSlug}
          title={submittedQuery || 'Wine country trip'}
          shareUrl={shareUrl}
        />
      </div>

      {myTrips.length > 0 && (
        <div className="relative ml-auto">
          <button
            type="button"
            onClick={() => setOpenTrips((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-600 hover:text-[#1a1a2e] bg-gray-100 hover:bg-gray-200 rounded-full px-2.5 py-1 transition-colors"
            data-testid="my-trips-toggle"
            aria-haspopup="listbox"
            aria-expanded={openTrips}
          >
            My saved trips ({myTrips.length})
            <ChevronDown className={`w-3 h-3 transition-transform ${openTrips ? 'rotate-180' : ''}`} />
          </button>
          {openTrips && (
            <div
              className="absolute right-0 mt-1.5 w-64 max-h-72 overflow-y-auto bg-white rounded-lg shadow-xl border border-gray-200 py-1.5 z-30"
              role="listbox"
              data-testid="my-trips-dropdown"
            >
              {myTrips.map((t) => (
                <div
                  key={t.slug}
                  className="group/trip flex items-stretch hover:bg-gold-50 transition-colors"
                  data-testid={`my-trip-row-${t.slug}`}
                >
                  <Link
                    to={`/trip/${t.slug}`}
                    onClick={() => setOpenTrips(false)}
                    className="flex-1 min-w-0 block px-3 py-2"
                    data-testid={`my-trip-item-${t.slug}`}
                  >
                    <div className="text-xs text-[#1a1a2e] font-medium truncate" title={t.query}>
                      {t.query || 'Untitled trip'}
                    </div>
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      /trip/{t.slug}
                      {t.created_at &&
                        ` · ${new Date(t.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                    </div>
                  </Link>
                  <button
                    type="button"
                    onClick={(e) => handleDelete(t.slug, e)}
                    disabled={deletingSlug === t.slug}
                    className="shrink-0 flex items-center justify-center px-2.5 text-gray-300 hover:text-wine-700 hover:bg-wine-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label={`Delete trip ${t.query || t.slug}`}
                    data-testid={`my-trip-delete-${t.slug}`}
                    title="Delete this saved trip"
                  >
                    {deletingSlug === t.slug ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SaveTripBar;
